import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["input", "preview", "signedId", "progress", "remove"]
  static values = {
    url: { type: String, default: "/rails/active_storage/direct_uploads" },
    signedId: String
  }

  connect() {
    if (this.hasSignedIdValue && this.signedIdValue) {
      this.showExistingPreview()
    }
  }

  upload(event) {
    const file = this.inputTarget.files[0]
    if (!file) return

    const validTypes = ["image/png", "image/jpeg", "image/gif", "image/webp"]
    if (!validTypes.includes(file.type)) {
      this.showError("Please select a valid image file (PNG, JPEG, GIF, or WebP)")
      this.inputTarget.value = ""
      return
    }

    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      this.showError("Image must be less than 5MB")
      this.inputTarget.value = ""
      return
    }

    this.showProgress()

    const upload = new DirectUpload(file, this.urlValue, this)

    upload.create((error, blob) => {
      if (error) {
        console.error("Direct upload error:", error)
        this.hideProgress()
        this.showError("Failed to upload image. Please try again.")
      } else {
        this.signedIdTarget.value = blob.signed_id
        this.signedIdValue = blob.signed_id
        this.showPreview(file)
        this.hideProgress()
      }
    })
  }

  showError(message) {
    if (window.showToast) {
      window.showToast(message, "error")
    } else {
      // Fallback to inline error display
      this.showInlineError(message)
    }
  }

  showInlineError(message) {
    // Create or update inline error message
    let errorEl = this.element.querySelector(".upload-error")
    if (!errorEl) {
      errorEl = document.createElement("p")
      errorEl.className = "upload-error text-sm text-red-600 mt-2"
      errorEl.setAttribute("role", "alert")
      errorEl.setAttribute("aria-live", "polite")
      this.inputTarget.parentNode.insertBefore(errorEl, this.inputTarget.nextSibling)
    }
    errorEl.textContent = message

    // Clear error after 5 seconds
    setTimeout(() => {
      if (errorEl && errorEl.parentNode) {
        errorEl.remove()
      }
    }, 5000)
  }

  // Required DirectUpload delegate method for progress tracking
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", (event) => {
      const progress = (event.loaded / event.total) * 100
      this.updateProgress(progress)
    })
  }

  showProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove("hidden")
      this.updateProgress(0)
    }
  }

  hideProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.add("hidden")
    }
  }

  updateProgress(percent) {
    if (this.hasProgressTarget) {
      const bar = this.progressTarget.querySelector("[data-progress-bar]")
      if (bar) {
        bar.style.width = `${percent}%`
      }
    }
  }

  showPreview(file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      if (this.hasPreviewTarget) {
        this.previewTarget.innerHTML = `
          <img src="${e.target.result}" class="w-full max-w-md h-40 object-cover rounded-lg" alt="Cover image preview">
          <p class="text-sm text-gray-500 mt-1">New cover image</p>
        `
        this.previewTarget.classList.remove("hidden")
      }
      if (this.hasRemoveTarget) {
        this.removeTarget.classList.remove("hidden")
      }
    }
    reader.readAsDataURL(file)
  }

  showExistingPreview() {
    if (this.hasPreviewTarget && this.hasSignedIdTarget && this.signedIdTarget.value) {
      this.previewTarget.innerHTML = `
        <div class="w-full max-w-md h-40 bg-gray-100 rounded-lg flex items-center justify-center">
          <span class="text-gray-500">Image uploaded (will be attached on save)</span>
        </div>
        <p class="text-sm text-gray-500 mt-1">Cover image ready</p>
      `
      this.previewTarget.classList.remove("hidden")
      if (this.hasRemoveTarget) {
        this.removeTarget.classList.remove("hidden")
      }
    }
  }

  remove(event) {
    event.preventDefault()
    
    if (this.hasSignedIdTarget) {
      this.signedIdTarget.value = ""
    }
    this.signedIdValue = ""
    
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }
    
    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML = ""
      this.previewTarget.classList.add("hidden")
    }
    
    if (this.hasRemoveTarget) {
      this.removeTarget.classList.add("hidden")
    }
  }
}
